import { Helmet } from 'react-helmet-async';
import { APP_NAME } from '@/lib/constants';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product' | 'profile';
}

export const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website' 
}: SEOProps) => {
  const siteName = APP_NAME;
  const BASE_DOMAIN = "https://feevho.com";
  
  // URL Resolution - Safe for Prerendering/SSR where window is undefined
  let currentUrl = url;
  if (!currentUrl && typeof window !== 'undefined') {
    currentUrl = `${BASE_DOMAIN}${window.location.pathname}`;
  } else if (!currentUrl) {
    currentUrl = BASE_DOMAIN;
  }

  const defaultImage = `${BASE_DOMAIN}/feevho-pic.png`; 
  const finalImage = image && image.startsWith('http') ? image : defaultImage;
  
  // Dynamic Title Fallback
  const finalTitle = title ? `${title} | ${siteName}` : `${siteName} | קטלוג מערכות דיגיטליות מתקדם`;
  
  // Description sanitization & Fallback
  const stripHtml = (html: string) => html.replace(/<[^>]*>?/gm, '').replace(/\n/g, ' ').trim();
  const cleanDescription = description 
    ? stripHtml(description) 
    : "הפלטפורמה המובילה לגילוי, שיתוף וסקירת מערכות דיגיטליות חכמות. גלו כלי AI ופתרונות מתקדמים שמחברים בין יוצרים למשתמשים.";
  
  const metaDescription = cleanDescription.length > 160 
    ? `${cleanDescription.substring(0, 157)}...` 
    : cleanDescription;

  return (
    <Helmet prioritizeSeoTags>
      <title>{finalTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph / Facebook / WhatsApp */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:secure_url" content={finalImage} />
      <meta property="og:image:alt" content={title || siteName} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="he_IL" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={finalImage} />
    </Helmet>
  );
};